
import { SalesTransaction, AttributionDimension } from "./types";

/**
 * Interface for attribution rule definitions
 */
export interface AttributionRule {
  dimension: AttributionDimension;
  description: string;
  apply: (transaction: SalesTransaction) => string;
}

/**
 * Geography attribution rule
 */
export const geographyAttributionRule: AttributionRule = {
  dimension: AttributionDimension.GEOGRAPHY,
  description: 'Use ship-to geography unless otherwise stated',
  apply: (transaction: SalesTransaction): string => {
    return transaction.shipToCountry;
  }
};

/**
 * Account attribution rule
 */
export const accountAttributionRule: AttributionRule = {
  dimension: AttributionDimension.ACCOUNT,
  description: 'Direct sales = customer; Channel sales = reseller unless sell-through',
  apply: (transaction: SalesTransaction): string => {
    // If it's a channel attribution, we'd need additional logic to determine end customer
    if (transaction.attributionType === 'Channel') {
      return `${transaction.accountId} (Channel)`;
    }
    return transaction.accountId;
  }
};

/**
 * Product attribution rule
 */
export const productAttributionRule: AttributionRule = {
  dimension: AttributionDimension.PRODUCT,
  description: 'Use SKU-level mapping or component breakdown in bundles',
  apply: (transaction: SalesTransaction): string => {
    return transaction.productId;
  }
};

/**
 * Time Period attribution rule
 */
export const timePeriodAttributionRule: AttributionRule = {
  dimension: AttributionDimension.TIME_PERIOD,
  description: 'Use invoice date unless accrual/revenue recognition differs',
  apply: (transaction: SalesTransaction): string => {
    const date = new Date(transaction.date);
    return `${date.getFullYear()}-Q${Math.floor(date.getMonth() / 3) + 1}`;
  }
};

export const attributionRules: AttributionRule[] = [
  geographyAttributionRule,
  accountAttributionRule,
  productAttributionRule,
  timePeriodAttributionRule
];

/**
 * Applies all attribution rules to a transaction
 */
export function applyAttributionRules(transaction: SalesTransaction): Record<AttributionDimension, string> {
  return {
    [AttributionDimension.GEOGRAPHY]: geographyAttributionRule.apply(transaction),
    [AttributionDimension.ACCOUNT]: accountAttributionRule.apply(transaction),
    [AttributionDimension.PRODUCT]: productAttributionRule.apply(transaction),
    [AttributionDimension.TIME_PERIOD]: timePeriodAttributionRule.apply(transaction),
  };
}
